import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ListingClient,
  ListingStatus,
  ListingStatusInfo,
} from 'src/WebApiController';

export type IListingStatusContext = {
  getOrSubscribeForListingStatus: (
    listingId: number
  ) => ListingStatusInfo | undefined;
  isLoading: boolean;
};

export const ListingStatusContext = React.createContext<IListingStatusContext>({
  getOrSubscribeForListingStatus: () => undefined,
  isLoading: false,
});

export const useListingStatusContext = () => useContext(ListingStatusContext);

export const ListingStatusContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();

  // State to track listing statuses
  const [listingStatuses, setListingStatuses] = useState<
    Map<number, ListingStatusInfo>
  >(new Map());
  const [isLoading, setIsLoading] = useState(false);

  // Tracking subscribed listing IDs
  const subscribedListingIds = useRef<Set<number>>(new Set());
  const pendingListingIds = useRef<Set<number>>(new Set());
  const batchTimer = useRef<NodeJS.Timeout | null>(null);

  const BATCH_INTERVAL_MS = 4000; // 4 seconds
  const MAX_BATCH_SIZE = 1000; // Limit batch size for API performance

  const listingClient = useMemo(
    () => new ListingClient(activeAccountWebClientConfig),
    [activeAccountWebClientConfig]
  );

  // Unsubscribe from a listing status
  const unsubscribeFromListingStatus = useCallback((listingId: number) => {
    console.debug(`Unsubscribing from listing status: ${listingId}`);

    // Remove from subscribed set
    subscribedListingIds.current.delete(listingId);

    // Remove from pending as well
    pendingListingIds.current.delete(listingId);
  }, []);

  // Function to fetch listing statuses in batch
  const fetchBatchedListingStatuses = useCallback(() => {
    if (pendingListingIds.current.size === 0) return;
    if (isLoading) return; // Avoid overlapping calls

    const idsToFetch = Array.from(pendingListingIds.current);
    pendingListingIds.current.clear(); // Clear pending IDs

    // Split into smaller batches if needed
    const batches = [];
    for (let i = 0; i < idsToFetch.length; i += MAX_BATCH_SIZE) {
      batches.push(idsToFetch.slice(i, i + MAX_BATCH_SIZE));
    }

    console.debug(
      `Fetching listing statuses for ${idsToFetch.length} listings in ${batches.length} batch(es)`
    );

    setIsLoading(true);

    // Process all batches
    Promise.all(
      batches.map(async (batchIds) => {
        return tryInvokeApi(
          async () => {
            const statuses =
              await listingClient.getListingStatusByListingIds(batchIds);
            return { batchIds, statuses };
          },
          (error) => {
            console.warn(
              'Failed to fetch listing statuses for batch:',
              batchIds,
              error
            );
            trackError('ListingClient.getListingStatusByListingIds', error, {
              trackErrorData: { listingIds: batchIds },
            });
            return { batchIds, statuses: [] };
          }
        );
      })
    ).then((results) => {
      // Merge all results into the state
      setListingStatuses((prevStatuses) => {
        const newStatuses = new Map(prevStatuses);

        results.forEach((result) => {
          if (result?.statuses) {
            result.statuses.forEach((status) => {
              if (status.id) {
                if (
                  status.status !== ListingStatus.ListingPending &&
                  status.status !== ListingStatus.DelistingPending
                ) {
                  // For any status we get that is no longer pending, unsubscribe
                  unsubscribeFromListingStatus(status.id);
                }
                newStatuses.set(status.id, status);
              }
            });
          }
        });

        return newStatuses;
      });

      setIsLoading(false);
    });
  }, [isLoading, listingClient, trackError, unsubscribeFromListingStatus]);

  // Subscribe to a listing status
  const subscribeToListingStatus = useCallback(
    (listingId: number) => {
      console.debug(`Subscribing to listing status: ${listingId}`);

      // Add to subscribed set
      subscribedListingIds.current.add(listingId);

      // Add to pending for immediate fetch if not already cached
      if (!listingStatuses.has(listingId)) {
        pendingListingIds.current.add(listingId);

        // Trigger immediate fetch if buffer gets large enough
        if (pendingListingIds.current.size >= 10) {
          console.debug(
            'Pending buffer reached threshold, fetching immediately'
          );
          fetchBatchedListingStatuses();
        }
      }
    },
    [listingStatuses, fetchBatchedListingStatuses]
  );

  // Set up batch timer
  useEffect(() => {
    const startBatchTimer = () => {
      if (batchTimer.current) {
        clearInterval(batchTimer.current);
      }

      batchTimer.current = setInterval(() => {
        // Only fetch if we have subscribed listings
        if (subscribedListingIds.current.size > 0) {
          // Add all subscribed IDs to pending (for periodic refresh)
          subscribedListingIds.current.forEach((id) => {
            pendingListingIds.current.add(id);
          });

          fetchBatchedListingStatuses();
        }
      }, BATCH_INTERVAL_MS);
    };

    startBatchTimer();

    // Cleanup on unmount
    return () => {
      if (batchTimer.current) {
        clearInterval(batchTimer.current);
        batchTimer.current = null;
      }
    };
  }, [fetchBatchedListingStatuses]);

  // Get listing status from cache
  const getOrSubscribeForListingStatus = useCallback(
    (listingId: number): ListingStatusInfo | undefined => {
      const status = listingStatuses.get(listingId);
      if (status == null) {
        subscribeToListingStatus(listingId);
        return undefined;
      } else {
        if (
          status.status !== ListingStatus.ListingPending &&
          status.status !== ListingStatus.DelistingPending
        ) {
          // If the status indicates we are done (e.g. not pending), unsubscribe
          unsubscribeFromListingStatus(listingId);
        }

        // Remove from cached statuses
        // If we still subscribe, we will refresh periodically
        setListingStatuses((prevStatuses) => {
          const newStatuses = new Map(prevStatuses);
          newStatuses.delete(listingId);
          return newStatuses;
        });
      }
      return status;
    },
    [listingStatuses, subscribeToListingStatus, unsubscribeFromListingStatus]
  );

  // Clean up old cached statuses periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      setListingStatuses((prevStatuses) => {
        const newStatuses = new Map();

        // Only keep statuses for subscribed listings
        prevStatuses.forEach((status, listingId) => {
          if (subscribedListingIds.current.has(listingId)) {
            newStatuses.set(listingId, status);
          }
        });

        const removedCount = prevStatuses.size - newStatuses.size;
        if (removedCount > 0) {
          console.debug(
            `Cleaned up ${removedCount} unused listing statuses from cache`
          );
        }

        return newStatuses;
      });
    }, 60000); // Clean up every minute

    return () => clearInterval(cleanupInterval);
  }, []);

  return (
    <ListingStatusContext.Provider
      value={{
        getOrSubscribeForListingStatus,
        isLoading,
      }}
    >
      {children}
    </ListingStatusContext.Provider>
  );
};
